/*
 * illmn-ad-attribution.js — The Illuminatee
 *
 * Records WHICH product ad the shopper clicked, on the order.
 *
 * The catalog ads land on a COLLECTION page, so Shopify records the collection
 * as the landing page and the "which product ad did they click?" signal is lost
 * from the back end. This reads the ad signals off the landing URL and writes
 * them onto the CART as attributes; Shopify carries cart attributes through to
 * the ORDER, where they appear in the admin under "Additional details" — on any
 * plan, with no app installed. Names are "_"-prefixed so Shopify hides them from
 * the customer in cart, checkout and confirmation emails.
 *
 *   _Ad Product      Cosmic Geometry T-Shirt - Military Green
 *   _Ad Product URL  /products/cosmic-geometry-t-shirt?variant=56609511145798
 *   _Ad Campaign ID  120249439718470786
 *   _Ad ID           120249439718480786
 *   _Ad Source       facebook
 *   _Ad Landed       2026-08-13T09:14:22Z
 *
 * Last-touch: a later ad click in the same session replaces the earlier one.
 * Ordinary browsing (a page with no ad parameters) never clears it.
 *
 * PROVENANCE — restored 8 Sep 2026. This shipped 13 Aug 2026 as the second half
 * of assets/illmn-ad-pin.js. The BuildMyPOD port (theme 6.1, 1 Sep 08:52)
 * reimplemented the PINNER as assets/bmp-curated-first.js, did not carry this
 * attribution half across, and deleted illmn-ad-pin.js — so from 1 Sep no order
 * recorded an _Ad Landed (last capture: order #1050, 31 Aug 18:23:42Z).
 *
 * This file restores ONLY the attribution half. Do NOT re-add the old pinner
 * alongside it: bmp-curated-first.js owns pinning now and reads the SAME pin
 * parameter, so shipping both would inject the pinned card twice.
 *
 * Two deliberate changes from the 5.1 original: the pin parameter name comes
 * from this script tag's data-pin-param (fed by settings.bmp_curated_first_param)
 * instead of being hardcoded, so it cannot drift out of sync with the pinner;
 * and the fetch hook also accepts a URL object, which only widens matching.
 * Everything else is unchanged from the version that ran 13 Aug – 1 Sep 2026.
 */
(function () {
  'use strict';
  if (window.__illmnAdAttrInit) return;
  window.__illmnAdAttrInit = true;
  if (window.Shopify && window.Shopify.designMode) return; // not in the theme editor

  var KEY = 'illmnAdAttr';
  var qp = new URLSearchParams(window.location.search);

  /* The ad links use the same parameter the collection pinner reads, so take
     the name from the loader rather than hardcoding it. */
  var PIN_PARAM = (function () {
    var el = document.querySelector('script[data-illmn-ad-attribution]');
    var name = el && el.dataset ? String(el.dataset.pinParam || '').trim() : '';
    return name || 'pin';
  })();

  function val(n) { var v = qp.get(n); return v ? String(v).trim() : ''; }
  function digits(s) { return /^\d+$/.test(s || '') ? s : ''; }
  function store(o) { try { sessionStorage.setItem(KEY, JSON.stringify(o)); } catch (e) {} }
  function load() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }

  /* ---- 1. capture the signals off the landing URL ---------------------- */
  /* The pin value may be a bare handle or a full, possibly percent-encoded,
     product URL — Facebook's catalog deep-link override emits the latter. */
  function parsePin() {
    var raw = qp.get(PIN_PARAM);
    if (!raw) return { handle: '', variant: '' };
    var s = raw.split(',')[0];
    try { s = decodeURIComponent(s); } catch (e) {}
    s = s.trim();
    var qi = s.indexOf('?');
    var query = qi > -1 ? s.slice(qi + 1) : '';
    var path = (qi > -1 ? s.slice(0, qi) : s).replace(/^https?:\/\/[^\/]*/i, '').split('#')[0];
    var handle = '';
    var pi = path.indexOf('/products/');
    if (pi > -1) {
      handle = path.slice(pi + 10).split('/')[0];
    } else {
      var parts = path.split('/').filter(Boolean);
      handle = parts.pop() || '';
    }
    var variant = '';
    if (query) {
      var m = /(?:^|&)variant=([^&]*)/.exec(query);
      if (m) { try { variant = digits(decodeURIComponent(m[1])); } catch (e) {} }
    }
    return { handle: handle.split('?')[0].split('#')[0], variant: variant };
  }

  /* If there is no pin param at all, the product may still be knowable from the
     landing path itself. Catalog ads whose feed URL points straight at the
     product page (and static ads linked to a product) land on /products/<handle>
     with no pin parameter — those would otherwise record "(no pinned product)"
     even though the product was sitting right there in the URL. */
  function pathProduct() {
    var p = window.location.pathname || '';
    var i = p.indexOf('/products/');
    if (i < 0) return '';
    return p.slice(i + 10).split('/')[0].split('?')[0].split('#')[0];
  }

  var pin = parsePin();
  var handle = pin.handle || pathProduct();
  /* Meta appends the real variant id as a SIBLING param, not inside the pin value. */
  var variantId = digits(val('pinv')) || pin.variant || digits(val('variant'));
  var source = val('utm_source');
  var campaign = val('campaign_id') || val('utm_campaign');
  var adId = val('ad_id') || val('utm_content');
  /* NOTE: the trigger is a real ad signal only — deliberately pin.handle and
     NOT the path-derived handle, otherwise every ORGANIC visitor browsing a
     product page would be falsely attributed to an ad. The landing path only
     enriches attribution that an ad parameter has already established. */
  var hasAdSignal = !!(pin.handle || source || campaign || adId || val('fbclid'));

  if (hasAdSignal) {
    store({
      handle: handle,
      variant: variantId,
      source: source || (val('fbclid') ? 'facebook' : ''),
      campaign: campaign,
      adId: adId,
      landed: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
      label: '',        /* filled in below, once the product has been looked up */
      applied: false
    });
  }

  var data = load();
  if (!data) return;    /* ordinary visitor, nothing to attribute */

  /* ---- 2. turn the handle into something readable in the admin --------- */
  function withLabel(d, cb) {
    if (!d.handle || d.label) return cb(d);
    fetch('/products/' + encodeURIComponent(d.handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (p) {
          var label = p.title || d.handle;
          if (d.variant) {
            var v = (p.variants || []).filter(function (x) {
              return String(x.id) === String(d.variant);
            })[0];
            /* variant titles look like "Military Green / S" — keep the colour */
            if (v && v.title) label += ' - ' + String(v.title).split(' / ')[0];
          }
          d.label = label;
          store(d);
        }
        cb(d);
      })
      .catch(function () { cb(d); });
  }

  /* ---- 3. write them onto the cart ------------------------------------- */
  function attrs(d) {
    var a = {};
    a['_Ad Product'] = d.label || d.handle || '(no pinned product)';
    if (d.handle) {
      a['_Ad Product URL'] = '/products/' + d.handle + (d.variant ? '?variant=' + d.variant : '');
    }
    if (d.campaign) a['_Ad Campaign ID'] = d.campaign;
    if (d.adId) a['_Ad ID'] = d.adId;
    if (d.source) a['_Ad Source'] = d.source;
    a['_Ad Landed'] = d.landed;
    return a;
  }

  /* /cart/update.js merges the keys it is given and leaves any other cart
     attributes alone, so this never clobbers anything else. */
  function push(d) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs(d) })
    })
      .then(function () { d.applied = true; store(d); })
      .catch(function () {});
  }

  if (!data.applied) withLabel(data, push);

  /* ---- 4. re-apply once the cart actually has something in it ----------
     Attributes set against an empty cart normally persist, but re-applying
     after a real add-to-cart makes this robust if the cart was only created
     (or was reset) later in the session.

     The wrapper is a passthrough: it forwards every argument untouched and
     returns the ORIGINAL promise, so callers see exactly what they would
     without it. The observation runs on a DERIVED chain whose rejection is
     swallowed, so a failed cart request can never surface as an extra
     unhandled rejection. /cart/update is excluded from the match so this can
     never trigger itself. */
  if (window.fetch && !window.__illmnAdAttrHook) {
    window.__illmnAdAttrHook = true;
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = '';
      if (typeof input === 'string') {
        url = input;
      } else if (input) {
        url = typeof input.url === 'string' ? input.url
            : (typeof input.href === 'string' ? input.href : '');
      }
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|clear)/.test(url)) {
        promise
          .then(function () {
            var d = load();
            if (d) withLabel(d, push);
          })
          .catch(function () {});
      }
      return promise;
    };
  }
})();
